import {
    PagedResult,
    ReportFilters,
    ReportType,
    getReportDataPaged,
} from '@/lib/api/reports/reports-api'
import { useEffect, useState } from 'react'
import { formatCurrency } from '../config/report-configs'

interface CommissionItemsPickerProps {
    type: ReportType
    label: string
    filters: Partial<ReportFilters>
    /** Ids fora do cálculo. Vale só para esta apuração: nada é marcado no atendimento. */
    excluded: Set<number>
    onToggle: (id: number) => void
}

const PAGE_SIZE = 10

/** Data e nome do que foi atendido, do jeito que cada tipo os guarda. */
function describe(row: any): string {
    return row.catalog?.name || row.title || '—'
}

function dateOf(row: any): string {
    const value = row.scheduledAt || row.createdAt
    return value ? new Date(value).toLocaleDateString('pt-BR') : '—'
}

/**
 * Atendimentos de um tipo, paginados, para o operador tirar algum do cálculo.
 *
 * **Paginado no banco**: um mês de agendamentos de uma pessoa passa de mil registros
 * com facilidade, e a lista existe justamente para achar aquele atendimento que não
 * deve ser comissionado — carregar tudo de uma vez trava a tela na hora de conferir.
 *
 * A exclusão vale só para esta apuração: nada é marcado no atendimento, e ele volta
 * inteiro na próxima vez que o relatório for aberto.
 */
export default function CommissionItemsPicker({
    type,
    label,
    filters,
    excluded,
    onToggle,
}: CommissionItemsPickerProps) {
    const [items, setItems] = useState<any[]>([])
    const [page, setPage] = useState(1)
    const [pageCount, setPageCount] = useState(1)
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        let active = true
        setLoading(true)
        getReportDataPaged(type, {
            ...(filters as ReportFilters),
            page,
            pageSize: PAGE_SIZE,
        })
            .then(res => {
                if (!active) return
                const result = res.result as PagedResult<any> | undefined
                setItems(result?.items ?? [])
                setTotal(result?.total ?? 0)
                setPageCount(result?.pageCount ?? 1)
            })
            .catch(() => {
                if (!active) return
                setItems([])
                setTotal(0)
                setPageCount(1)
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
        // `filters` é recriado a cada render do pai; comparar serializado evita laço.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, JSON.stringify(filters), page])

    return (
        <div className="border border-base-300 rounded-lg">
            <div className="px-3 py-2 flex items-center justify-between gap-2 border-b border-base-300">
                <span className="text-sm font-medium capitalize">{label}</span>
                <span className="text-xs text-base-content/60">
                    {total} no período
                </span>
            </div>

            <div className="overflow-x-auto">
                <table className="table table-xs">
                    <thead>
                        <tr>
                            <th
                                className="w-8"
                                title="Desmarque o que não entra nesta comissão"
                            >
                                Incl.
                            </th>
                            <th>Nº</th>
                            <th>Data</th>
                            <th>Paciente</th>
                            <th>Atendimento</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={5} className="text-center py-4">
                                    <span className="loading loading-spinner loading-sm" />
                                </td>
                            </tr>
                        ) : items.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={5}
                                    className="text-center py-4 text-base-content/60"
                                >
                                    Nenhum atendimento no período.
                                </td>
                            </tr>
                        ) : (
                            items.map(row => {
                                const isOut = excluded.has(row.id)
                                return (
                                    <tr
                                        key={row.id}
                                        className={isOut ? 'opacity-40' : ''}
                                    >
                                        <td>
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-xs"
                                                checked={!isOut}
                                                onChange={() => onToggle(row.id)}
                                                aria-label={`Incluir o atendimento ${row.id}`}
                                            />
                                        </td>
                                        <td>#{row.id}</td>
                                        <td>{dateOf(row)}</td>
                                        <td className="max-w-[9rem] truncate">
                                            {row.client?.name || '—'}
                                        </td>
                                        <td className="max-w-xs truncate">
                                            {describe(row)}
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {pageCount > 1 && (
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-base-300">
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        disabled={page <= 1 || loading}
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                        Anterior
                    </button>
                    <span className="text-xs text-base-content/60">
                        Página {page} de {pageCount}
                    </span>
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        disabled={page >= pageCount || loading}
                        onClick={() => setPage(p => Math.min(pageCount, p + 1))}
                    >
                        Próxima
                    </button>
                </div>
            )}
        </div>
    )
}
